import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  // Force light mode for ALL public routes (everything except /dashboard)
  const isLightOnlyFlow = !location.pathname.startsWith('/dashboard');

  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme') as Theme;
      return saved || 'light';
    }
    return 'light';
  });

  const applyTheme = (targetTheme: Theme, forceLight: boolean) => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');

    let actualTheme = targetTheme;
    if (forceLight) {
        actualTheme = 'light';
    } else if (targetTheme === 'system') {
      actualTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }

    root.classList.add(actualTheme);
    
    // Also manage meta theme-color for mobile battery bar/tabs
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', actualTheme === 'dark' ? '#0f172a' : '#f9fafb');
    }
  };

  useEffect(() => {
    applyTheme(theme, isLightOnlyFlow);
  }, [theme, isLightOnlyFlow]);

  // Listen for system changes if theme is 'system'
  useEffect(() => {
    if (theme !== 'system' || isLightOnlyFlow) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => applyTheme('system', false);

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, isLightOnlyFlow]);

  const setTheme = (newTheme: Theme) => {
    localStorage.setItem('theme', newTheme);
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
